import { useCallback, useEffect, useRef, useState } from "react";
import { cicloObter, type EstadoCiclo } from "@/api/metas.service";

/**
 * Por que a consulta falhou: `negado` é a resposta do servidor (quem não é
 * responsável recebe erro) e conta como "sem fluxo"; `transitorio` é rede,
 * timeout ou indisponibilidade — não diz nada sobre permissão e pede nova tentativa.
 */
export type ErroFluxo = "negado" | "transitorio";

const erroDe = (err: any): ErroFluxo => {
  const status = typeof err?.status === "number" ? err.status : 0;
  return status === 0 || status === 408 || status === 429 || status >= 502 ? "transitorio" : "negado";
};

/**
 * O ciclo de aprovação de um cenário, para as telas que precisam saber se o
 * fluxo governa o que elas fazem. Falha `negada` conta como "sem fluxo": a tela
 * segue o comportamento de sempre e o servidor continua sendo quem decide.
 * Ao recarregar o mesmo cenário o estado anterior é mantido até a resposta
 * chegar, para a tela não piscar nem travar; só trocar de cenário o zera.
 */
export function useFluxoCenario(clientId: string, cenarioId: string | undefined) {
  const [estado, setEstado] = useState<EstadoCiclo | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<ErroFluxo | null>(null);
  // Cresce a cada consulta: uma resposta que chega depois de o cenário ter mudado é descartada.
  const [versao, setVersao] = useState(0);
  const chaveAnterior = useRef("");

  const recarregar = useCallback(() => setVersao((v) => v + 1), []);

  useEffect(() => {
    const chave = `${clientId}|${cenarioId ?? ""}`;
    if (chaveAnterior.current !== chave) {
      chaveAnterior.current = chave;
      setEstado(null);
    }
    setErro(null);
    if (!cenarioId) return;
    let cancelado = false;
    setCarregando(true);
    Promise.resolve()
      .then(() => cicloObter(cenarioId, clientId))
      .then((resposta) => !cancelado && setEstado(resposta))
      .catch((err) => {
        if (cancelado) return;
        const tipo = erroDe(err);
        setErro(tipo);
        // Negado é resposta definitiva; transitório mantém o último estado conhecido.
        if (tipo === "negado") setEstado(null);
      })
      .finally(() => !cancelado && setCarregando(false));
    return () => {
      cancelado = true;
    };
  }, [clientId, cenarioId, versao]);

  return { estado, carregando, erro, recarregar };
}
